/**
 * One AI voice call: outcome, summary, hand-off, recording, transcript.
 * Used by Call History → "AI Assistant" tab and the AI Voice Calls page.
 * Data: GET /voice-agents/sessions/:id or GET /calls/:callId/ai-session.
 */
import { useState, type JSX } from 'react'
import { romanizeIndic } from '@kc3/types'
import { Bot, ChevronDown, ChevronRight, ClipboardList, PhoneForwarded, User, Wrench } from 'lucide-react'
import { CallRecordingPlayer } from '../reports/calls/CallRecordingPlayer.js'
import { formatDateTime } from '../../utils/formatDate.js'

export interface AiTurn {
  seq: number
  role: 'caller' | 'bot' | 'tool' | 'system'
  text: string | null
  toolName: string | null
  toolArgs: unknown
  toolResult: unknown
  tMs: number | null
}

export interface AiCollectedData {
  fields: { key: string; label: string; value: string | null; required: boolean }[]
  source: 'llm' | 'elevenlabs'
  extractedAt: string
}

export interface AiSession {
  id: string
  agentName: string | null
  agentVersion: number | null
  channel: 'pstn' | 'webrtc' | 'test'
  model: string | null
  provider?: string | null
  providerCallId?: string | null
  status: string
  outcome: 'resolved' | 'handed_off' | 'abandoned' | 'failed' | 'callback' | null
  endReason: string | null
  callerNumber: string | null
  callId: string | null
  handoffReason: string | null
  handoffSummary: string | null
  handoffQueueName: string | null
  handedToAgent: string | null
  summary: string | null
  startedAt: string
  endedAt: string | null
  durationSec: number | null
  recordingId: string | null
  collectedData?: AiCollectedData | null
  turns?: AiTurn[]
}

export const OUTCOME: Record<string, { label: string; cls: string }> = {
  resolved: { label: 'Resolved by AI', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  handed_off: { label: 'Handed to agent', cls: 'bg-blue-50 text-blue-700 ring-blue-200' },
  callback: { label: 'Callback requested', cls: 'bg-violet-50 text-violet-700 ring-violet-200' },
  abandoned: { label: 'Caller left', cls: 'bg-slate-100 text-slate-600 ring-slate-200' },
  failed: { label: 'Failed', cls: 'bg-red-50 text-red-700 ring-red-200' },
}

export const TOOL_LABEL: Record<string, string> = {
  search_knowledge: 'Searched knowledge',
  lookup_customer: 'Looked up caller',
  get_ticket_status: 'Checked ticket',
  transfer_to_human: 'Transferred to agent',
  end_call: 'Ended call',
  create_ticket: 'Created ticket',
}

export function fmtDuration(sec: number | null): string {
  if (sec === null || sec === undefined) return '—'
  const m = Math.floor(sec / 60)
  return `${m}:${String(sec % 60).padStart(2, '0')}`
}

const stamp = (ms: number | null): string => (ms === null ? '' : fmtDuration(Math.floor(ms / 1000)))

export function OutcomeChip({ outcome, status }: { outcome: AiSession['outcome']; status: string }): JSX.Element {
  const o = outcome ? OUTCOME[outcome] : null
  return (
    <span className={`inline-flex items-center rounded-[3px] px-2 py-0.5 text-[11px] font-semibold ring-1 ${o?.cls ?? 'bg-amber-50 text-amber-700 ring-amber-200'}`}>
      {o?.label ?? (status === 'ACTIVE' ? 'In progress' : status)}
    </span>
  )
}

function ToolChip({ t }: { t: AiTurn }): JSX.Element {
  const [open, setOpen] = useState(false)
  const ok = !(t.toolResult && typeof t.toolResult === 'object' && ((t.toolResult as Record<string, unknown>)['ok'] === false || (t.toolResult as Record<string, unknown>)['found'] === false))
  return (
    <div className="mx-auto max-w-[85%]">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ${ok ? 'bg-slate-50 text-slate-600 ring-slate-200' : 'bg-amber-50 text-amber-700 ring-amber-200'}`}>
        <Wrench className="h-3 w-3" /> {TOOL_LABEL[t.toolName ?? ''] ?? t.toolName} <span className="text-slate-400">{stamp(t.tMs)}</span>
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>
      {open && (
        <pre className="mt-1 max-h-48 overflow-auto rounded-[3px] bg-slate-900 p-2 text-[11px] text-slate-100">
          {JSON.stringify({ args: t.toolArgs, result: t.toolResult }, null, 2)}
        </pre>
      )}
    </div>
  )
}

/** "Data to collect" values, laid out like a filled-in form. */
function CollectedForm({ data }: { data: AiCollectedData }): JSX.Element {
  const missing = data.fields.filter((f) => f.required && !f.value).length
  return (
    <section className="rounded-[3px] border border-slate-200 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          <ClipboardList className="h-3.5 w-3.5" /> Collected information
        </p>
        <span className="text-[11px] text-slate-400">
          {missing > 0 && <span className="mr-2 font-semibold text-amber-700">{missing} required not given</span>}
          Extracted from the conversation{data.source === 'elevenlabs' ? ' by ElevenLabs' : ''}
        </span>
      </div>
      <dl className="grid gap-x-4 gap-y-2.5 sm:grid-cols-2">
        {data.fields.map((f) => (
          <div key={f.key} className="min-w-0">
            <dt className="text-[11.5px] font-medium text-slate-500">{f.label}{f.required && <span className="text-red-500"> *</span>}</dt>
            <dd className={`mt-0.5 rounded-[3px] px-2.5 py-1.5 text-[13px] ring-1 ${f.value ? 'bg-white text-slate-900 ring-slate-200' : f.required ? 'bg-amber-50 italic text-amber-700 ring-amber-200' : 'bg-slate-50 italic text-slate-400 ring-slate-200'}`}>
              <span className="whitespace-pre-line break-words">{f.value ?? 'Not given'}</span>
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

export function AiCallPanel({ session, showRecording = true }: { session: AiSession; showRecording?: boolean }): JSX.Element {
  const turns = session.turns ?? []
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-slate-600">
        <OutcomeChip outcome={session.outcome} status={session.status} />
        <span className="inline-flex items-center gap-1"><Bot className="h-3.5 w-3.5" /> {session.agentName ?? 'AI agent'}{session.agentVersion ? ` · v${session.agentVersion}` : ''}</span>
        <span>· {session.channel === 'pstn' ? 'Phone' : session.channel === 'webrtc' ? 'Web widget' : 'Test call'}</span>
        <span>· {formatDateTime(session.startedAt)}</span>
        <span>· {fmtDuration(session.durationSec)}</span>
      </div>

      <section className="rounded-[3px] border border-slate-200 bg-slate-50/60 p-3">
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">AI summary</p>
        <p className="text-[13px] leading-relaxed text-slate-800">{session.summary ?? (session.status === 'ACTIVE' ? 'The call is still in progress.' : 'No summary was generated for this call.')}</p>
      </section>

      {session.collectedData && session.collectedData.fields.length > 0 && <CollectedForm data={session.collectedData} />}

      {session.outcome === 'handed_off' && (
        <section className="rounded-[3px] border border-blue-200 bg-blue-50/60 p-3 text-[12.5px] text-blue-900">
          <p className="mb-1 flex items-center gap-1.5 font-semibold"><PhoneForwarded className="h-4 w-4" /> Handed to {session.handedToAgent ?? 'an agent'}{session.handoffQueueName ? ` via ${session.handoffQueueName}` : ''}</p>
          {session.handoffReason && <p><b>Reason:</b> {session.handoffReason}</p>}
          {session.handoffSummary && <p className="mt-1"><b>Note for the agent:</b> {session.handoffSummary}</p>}
        </section>
      )}

      {showRecording && session.recordingId && (
        <CallRecordingPlayer recordingId={session.recordingId} source={session.callerNumber ?? 'Caller'} destination={session.agentName ?? 'AI agent'}
          channelLabels={['Caller', 'AI agent']} fileName={`ai-call-${session.id}.wav`} />
      )}
      {!session.recordingId && session.provider === 'elevenlabs' && session.providerCallId && session.status !== 'ACTIVE' && (
        <section>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Recording</p>
          {/* Same-origin cookie auth, like CallRecordingPlayer's stream fetch. */}
          <audio controls preload="none" className="w-full" src={`/api/voice-agents/sessions/${session.id}/audio`} />
        </section>
      )}

      <section>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Transcript</p>
        {turns.length === 0 ? <p className="text-[12.5px] text-slate-500">No transcript lines were recorded.</p> : (
          <div className="space-y-2">
            {turns.map((t) => t.role === 'tool' ? <ToolChip key={t.seq} t={t} /> : (
              <div key={t.seq} className={`flex gap-2 ${t.role === 'caller' ? 'flex-row-reverse' : ''}`}>
                <span className={`mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${t.role === 'caller' ? 'bg-slate-200 text-slate-600' : 'bg-blue-100 text-blue-700'}`}>
                  {t.role === 'caller' ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                </span>
                <div className={`max-w-[80%] rounded-[3px] px-3 py-2 text-[13px] leading-relaxed ${t.role === 'caller' ? 'bg-slate-100 text-slate-800' : 'bg-blue-50 text-slate-800'}`}>
                  {t.text ? romanizeIndic(t.text) : t.text}
                  <span className="ml-2 text-[10.5px] text-slate-400">{stamp(t.tMs)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
